<template>
  <div class="products-select">
    <div v-if="selectedProducts.length > 0" class="product-list">
      <woot-label
        v-for="item in selectedProducts"
        :key="item.id"
        :title="item.title"
        :description="item.description"
        :show-close="!disabled"
        :color="item.color"
        variant="smooth"
        type="button"
        @click="onRemove(item)"
      />
    </div>

    <div v-if="!disabled" ref="dropdownWrapper" class="products-dropdown">
      <woot-button
        variant="smooth"
        class="product--add"
        icon="chevron-down"
        size="tiny"
        type="button"
        @click="toggleDropdown"
      >
        {{ $t('CONTACT_PANEL.PRODUCTS.CONVERSATION.ADD_BUTTON') }}
      </woot-button>

      <product-label-dropdown
        v-if="showDropdown"
        ref="dropdownPane"
        class="dropdown-pane"
        :class="{ 'dropdown-pane--up': openUpward }"
        :account-product-labels="availableProductLabels"
        :selected-product-labels="selectedProducts"
        @add="onAdd"
      />
    </div>
  </div>
</template>

<script>
import ProductLabelDropdown from 'shared/components/ui/productLabel/ProductLabelDropdown';

const CLIPPING_OVERFLOW = /(auto|scroll|hidden)/;

const visibleArea = element => {
  let top = 0;
  let bottom = window.innerHeight;
  let node = element.parentElement;
  while (node && node !== document.body) {
    if (CLIPPING_OVERFLOW.test(window.getComputedStyle(node).overflowY)) {
      const rect = node.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
    }
    node = node.parentElement;
  }
  return { top, bottom };
};

export default {
  name: 'OpportunityProductsSelect',
  components: {
    ProductLabelDropdown,
  },
  props: {
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    availableProductLabels: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDropdown: false,
      openUpward: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick, true);
  },
  methods: {
    async toggleDropdown() {
      if (this.disabled) return;

      this.showDropdown = !this.showDropdown;
      this.openUpward = false;
      if (!this.showDropdown) return;

      await this.$nextTick();
      this.choosePlacement();
    },
    choosePlacement() {
      const wrapper = this.$refs.dropdownWrapper;
      const pane = this.$refs.dropdownPane && this.$refs.dropdownPane.$el;
      if (!wrapper || !pane) return;

      const trigger = wrapper.getBoundingClientRect();
      const area = visibleArea(wrapper);
      const spaceBelow = area.bottom - trigger.bottom;
      const spaceAbove = trigger.top - area.top;
      this.openUpward =
        pane.offsetHeight > spaceBelow && spaceAbove > spaceBelow;
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    onAdd(productLabel) {
      if (this.disabled) return;
      this.$emit('add', productLabel);
    },
    onRemove(item) {
      if (this.disabled) return;
      this.$emit('remove', item);
    },
    handleOutsideClick(event) {
      if (!this.showDropdown) return;

      const wrapper = this.$refs.dropdownWrapper;

      if (wrapper && !wrapper.contains(event.target)) {
        this.closeDropdown();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.product-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
  max-height: 240px;
  overflow: auto;
  padding: var(--space-small);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-small);
}

.products-dropdown {
  position: relative;
}

.dropdown-pane {
  left: 0;
  position: absolute;
  top: 100%;
  visibility: visible;

  &.dropdown-pane--up {
    bottom: 100%;
    top: auto;
  }
  box-shadow: 0 0.8rem 1.6rem rgba(50, 50, 93, 0.08),
    0 0.4rem 1.2rem rgba(0, 0, 0, 0.07);
  border-radius: 0.8rem;
  border: 1px solid var(--s-50);
  padding: var(--space-small);
  z-index: var(--z-index-very-high);
  background-color: #fff;
  font-size: 1rem;
  width: fit-content;
  min-width: 20rem;
  box-sizing: border-box;

  // Vue merges this class onto ProductLabelDropdown's own root element,
  // so it's the same node as .dropdown-search-wrap, not an ancestor of it.
  &.dropdown-search-wrap {
    max-height: 70vh;
  }
}

.product--add {
  margin-bottom: var(--space-micro);
  margin-right: var(--space-micro);
}
</style>
